import { type ResultOf } from '@graphql-typed-document-node/core'
import { type TFunction } from 'i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'

import { applyOr, filterExists, findByPath } from '../generals'

import { conditionOperationLabelI18nKeyMap } from './conditionOperation'
import { matchSurveyFilter } from './matchSurveyFilter'
import { CustomerAttributeOp, QuestionOp } from './modelOperators'
import { formatNineSegment } from './segmentHelpers'
import { type NineNumber } from './segments'
import { type SurveyFilterGroups } from './surveyFilterGroups'
import { type FilterText } from './types'

const questionFragment = graphql(`
  fragment BuildSurveyFilterTextGroups_Question on Question {
    question_id
    question_text
  }
`)

const customerAttributeGroupFragment = graphql(`
  fragment BuildSurveyFilterTextGroups_CustomerAttributeGroup on CustomerAttributeGroup {
    id
    name
  }
`)

const brandGroupFragment = graphql(`
  fragment BuildSurveyFilterTextGroups_BrandGroup on BrandGroup {
    id
    name
    brands {
      id
      name
      number
    }
  }
`)

/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
type BuildSurveyFilterTextGroupsParams = {
  filterGroups: SurveyFilterGroups
  questions: ResultOf<typeof questionFragment>[]
  customerAttributeGroups: ResultOf<typeof customerAttributeGroupFragment>[]
  brandGroups: ResultOf<typeof brandGroupFragment>[]
  t: TFunction
}

/**
 * サーベイのフィルターグループの配列をフィルターテキストグループの配列に変換します。
 *
 * @param params - 変換に必要なパラメーター。
 * @returns フィルターテキストグループの配列。
 */
export function buildSurveyFilterTextGroups(
  params: BuildSurveyFilterTextGroupsParams,
): FilterText[][] {
  const { brandGroups, customerAttributeGroups, filterGroups, questions, t } =
    params

  return filterGroups.map((filterGroup) =>
    filterGroup.map((filter) =>
      matchSurveyFilter(filter, {
        question: (question) => ({
          name: R.pipe(
            questions,
            findByPath('question_id', question.questionId),
            applyOr(QuestionOp.toText, t('unknown_questionnaire_1')),
          ),
          subName: t(conditionOperationLabelI18nKeyMap[question.operation]),
          values: question.answerOptions.map(
            (answerOption) => `${answerOption.option} - ${answerOption.text}`,
          ),
        }),
        customerAttribute: (customerAttribute) => {
          const names = customerAttribute.customerAttributeIds.map(
            (customerAttributeId) =>
              CustomerAttributeOp.getNameFromGroups(
                customerAttributeGroups,
                customerAttributeId,
                t,
              ),
          )
          const customerAttributeGroupText =
            names[0]?.customerAttributeGroupName
          const customerAttributeTexts = names.map(
            R.prop('customerAttributeName'),
          )

          return {
            name: customerAttributeGroupText,
            subName: t(
              conditionOperationLabelI18nKeyMap[customerAttribute.operation],
            ),
            values: customerAttributeTexts,
          }
        },
        brandSegments: (brandSegments) => {
          const brandGroup = findByPath(
            brandGroups,
            'id',
            brandSegments.brandGroupId,
          )
          const brand = findByPath(
            filterExists(brandGroup?.brands),
            'number',
            brandSegments.brandNumber,
          )

          return {
            name: brandGroup?.name ?? t('unknown_brand_group'),
            subName: brand?.name ?? t('unknown_brand'),
            values: brandSegments.segmentNumbers.map((segmentNumber) =>
              formatNineSegment(segmentNumber as NineNumber, '%t1', t),
            ),
          }
        },
      }),
    ),
  )
}
